// ClaudeCode: Entry card component — displays one vault entry in the list
import type { EntryMeta } from '../api';

const TIER_LABELS: Record<number, { label: string; color: string }> = {
  4: { label: 'Trivial', color: '#6ee7b7' },
  3: { label: 'Convenience', color: '#93c5fd' },
  2: { label: 'Sensitive', color: '#fbbf24' },
  1: { label: 'Irreplaceable', color: '#f87171' },
};

interface EntryCardProps {
  entry: EntryMeta;
  onClick: (id: string) => void;
  onToggleFavorite: (id: string, current: boolean) => void;
}

export function EntryCard({ entry, onClick, onToggleFavorite }: EntryCardProps) {
  const tier = TIER_LABELS[entry.tier] ?? TIER_LABELS[4];

  return (
    <div
      onClick={() => onClick(entry.id)}
      style={{
        background: '#18181b',
        border: '1px solid #27272a',
        borderRadius: 8,
        padding: '14px 16px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#3f3f46')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#27272a')}
    >
      {/* Favorite star */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(entry.id, entry.favorite);
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 18,
          color: entry.favorite ? '#fbbf24' : '#52525b',
          padding: 0,
          lineHeight: 1,
        }}
        title={entry.favorite ? 'Unfavorite' : 'Favorite'}
      >
        {entry.favorite ? '\u2605' : '\u2606'}
      </button>

      {/* Title and meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 15,
            color: '#fafafa',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {entry.title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: '#71717a',
            marginTop: 2,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          {entry.category && <span>{entry.category}</span>}
          {entry.tags.length > 0 && (
            <span>{entry.tags.map((t) => `#${t}`).join(' ')}</span>
          )}
        </div>
      </div>

      {/* Tier badge */}
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: tier.color,
          border: `1px solid ${tier.color}33`,
          borderRadius: 4,
          padding: '2px 8px',
          whiteSpace: 'nowrap',
        }}
      >
        T{entry.tier}
      </span>
    </div>
  );
}
